/**
 * Structured Logger Utility
 *
 * Replaces direct console.log/error/warn usage with structured logging.
 * Provides environment-based filtering, JSON formatting for CloudWatch,
 * and consistent logging patterns across the application.
 *
 * Features:
 * - Log levels: debug, info, warn, error
 * - Environment-based filtering (development vs production)
 * - Structured JSON output for production (CloudWatch-ready)
 * - Human-readable colorized output for development
 * - Context support (service name, metadata)
 * - Automatic timestamp inclusion
 *
 * Usage:
 * ```javascript
 * import logger from '../utils/logger';
 *
 * // Basic logging
 * logger.info('User logged in', { userId: '123' });
 * logger.error('Failed to fetch data', { error: err.message });
 * logger.warn('Cache miss', { key: 'market_data' });
 * logger.debug('Processing item', { itemId: 42 });
 *
 * // Service-specific logger
 * const serviceLogger = logger.child('MarketDataService');
 * serviceLogger.info('Fetching data from Lambda', { url: lambdaUrl });
 * ```
 *
 * @module logger
 */

/**
 * Log levels in order of severity
 */
const LOG_LEVELS = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

/**
 * ANSI color codes for development console output
 */
const COLORS = {
  debug: '\x1b[36m', // Cyan
  info: '\x1b[32m',  // Green
  warn: '\x1b[33m',  // Yellow
  error: '\x1b[31m', // Red
  reset: '\x1b[0m',  // Reset
  dim: '\x1b[2m',    // Dim
  bold: '\x1b[1m',   // Bold
};

/**
 * Get current log level from environment
 * Development: debug (show all)
 * Production: info (hide debug)
 */
const getLogLevel = () => {
  // Support both Vite (import.meta.env) and Node.js (process.env) for testing
  // Prioritize process.env.NODE_ENV for test compatibility
  const env = process.env.NODE_ENV ||
              (typeof import.meta !== 'undefined' && import.meta.env?.MODE) ||
              'development';
  const configuredLevel = process.env.LOG_LEVEL ||
                          (typeof import.meta !== 'undefined' && import.meta.env?.VITE_LOG_LEVEL);

  if (configuredLevel && LOG_LEVELS[configuredLevel] !== undefined) {
    return configuredLevel;
  }

  return env === 'production' ? 'info' : 'debug';
};

/**
 * Check if a log level should be output
 * @param {string} level - Log level to check
 * @returns {boolean} - Whether this level should be logged
 */
const shouldLog = (level) => {
  const currentLevel = getLogLevel();
  return LOG_LEVELS[level] >= LOG_LEVELS[currentLevel];
};

/**
 * Format log entry for production (CloudWatch JSON format)
 * @param {string} level - Log level
 * @param {string} message - Log message
 * @param {Object} context - Additional context
 * @returns {string} - JSON formatted log entry
 */
const formatProductionLog = (level, message, context = {}) => {
  // Handle null/undefined context
  if (!context || typeof context !== 'object') {
    context = {};
  }

  const logEntry = {
    timestamp: new Date().toISOString(),
    level: level.toUpperCase(),
    message,
    context: context,
  };

  try {
    return safeStringify(logEntry);
  } catch (error) {
    // Fallback if stringify fails
    return JSON.stringify({
      timestamp: new Date().toISOString(),
      level: level.toUpperCase(),
      message,
      error: 'Failed to serialize context'
    });
  }
};

/**
 * Safely stringify an object, handling circular references
 * @param {*} obj - Object to stringify
 * @returns {string} - JSON string
 */
const safeStringify = (obj) => {
  const seen = new WeakSet();
  return JSON.stringify(obj, (key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return '[Circular]';
      }
      seen.add(value);
    }
    return value;
  }, 2);
};

/**
 * Format log entry for development (human-readable with colors)
 * @param {string} level - Log level
 * @param {string} message - Log message
 * @param {Object} context - Additional context
 * @returns {string} - Formatted log entry
 */
const formatDevelopmentLog = (level, message, context = {}) => {
  const color = COLORS[level] || COLORS.reset;
  const timestamp = new Date().toLocaleTimeString();
  const levelBadge = `${color}${COLORS.bold}[${level.toUpperCase()}]${COLORS.reset}`;
  const timeBadge = `${COLORS.dim}${timestamp}${COLORS.reset}`;

  let formattedMessage = `${timeBadge} ${levelBadge} ${message}`;

  // Handle null/undefined context
  if (!context || typeof context !== 'object') {
    return formattedMessage;
  }

  // Add service name if present
  if (context.service) {
    formattedMessage += ` ${COLORS.dim}(${context.service})${COLORS.reset}`;
  }

  // Add context object if present (excluding service name)
  const { service, ...restContext } = context;
  if (Object.keys(restContext).length > 0) {
    try {
      formattedMessage += `\n  ${COLORS.dim}${safeStringify(restContext)}${COLORS.reset}`;
    } catch (error) {
      formattedMessage += `\n  ${COLORS.dim}[Unable to stringify context]${COLORS.reset}`;
    }
  }

  return formattedMessage;
};

/**
 * Core logging function
 * @param {string} level - Log level
 * @param {string} message - Log message
 * @param {Object} context - Additional context
 */
const log = (level, message, context = {}) => {
  if (!shouldLog(level)) return;

  // Support both Vite (import.meta.env) and Node.js (process.env) for testing
  // Prioritize process.env.NODE_ENV for test compatibility
  const env = process.env.NODE_ENV ||
              (typeof import.meta !== 'undefined' && import.meta.env?.MODE) ||
              'development';
  const isProduction = env === 'production';

  const formattedLog = isProduction
    ? formatProductionLog(level, message, context)
    : formatDevelopmentLog(level, message, context);

  // Route to appropriate console method
  const consoleMethod = level === 'error' ? console.error :
                       level === 'warn' ? console.warn :
                       console.log;

  consoleMethod(formattedLog);
};

/**
 * Logger class
 */
class Logger {
  constructor(serviceName = null) {
    this.serviceName = serviceName;
  }

  /**
   * Create a child logger with a service name
   * @param {string} serviceName - Name of the service
   * @returns {Logger} - Child logger instance
   */
  child(serviceName) {
    return new Logger(serviceName);
  }

  /**
   * Add service context to log metadata
   * @param {Object} context - Log context
   * @returns {Object} - Context with service name
   */
  _addServiceContext(context) {
    if (this.serviceName) {
      return { service: this.serviceName, ...context };
    }
    return context;
  }

  /**
   * Log debug message (development only by default)
   * @param {string} message - Log message
   * @param {Object} context - Additional context
   */
  debug(message, context = {}) {
    log('debug', message, this._addServiceContext(context));
  }

  /**
   * Log info message
   * @param {string} message - Log message
   * @param {Object} context - Additional context
   */
  info(message, context = {}) {
    log('info', message, this._addServiceContext(context));
  }

  /**
   * Log warning message
   * @param {string} message - Log message
   * @param {Object} context - Additional context
   */
  warn(message, context = {}) {
    log('warn', message, this._addServiceContext(context));
  }

  /**
   * Log error message
   * @param {string} message - Log message
   * @param {Object|Error} context - Additional context or Error object
   */
  error(message, context = {}) {
    // Handle Error objects
    if (context instanceof Error) {
      context = {
        error: context.message,
        stack: context.stack,
        name: context.name,
      };
    } else if (context.error instanceof Error) {
      // Handle { error: Error } pattern
      const err = context.error;
      context = {
        ...context,
        error: err.message,
        stack: err.stack,
        name: err.name,
      };
    }

    log('error', message, this._addServiceContext(context));
  }

  /**
   * Measure execution time of an async function
   * @param {string} label - Label for the operation
   * @param {Function} fn - Async function to measure
   * @returns {Promise<*>} - Result of the function
   */
  async time(label, fn) {
    const start = performance.now();
    this.debug(`Starting: ${label}`);

    try {
      const result = await fn();
      const duration = Math.round(performance.now() - start);
      this.info(`Completed: ${label}`, { duration: `${duration}ms` });
      return result;
    } catch (error) {
      const duration = Math.round(performance.now() - start);
      this.error(`Failed: ${label}`, { duration: `${duration}ms`, error });
      throw error;
    }
  }
}

// Export singleton instance
const logger = new Logger();

export default logger;
export { Logger };
